// pdf-export-service.js
import jsPDF from 'jspdf';
import 'jspdf-autotable';

class PDFExportService {
    constructor() {
        this.primaryColor = [102, 126, 234]; // #667eea
        this.criticalColor = [220, 53, 69];
        this.highColor = [253, 126, 20];
        this.mediumColor = [255, 193, 7];
        this.lowColor = [40, 167, 69];
        this.prerequisiteColor = [0, 102, 204];
    }

    /**
     * Main export function
     * @param {Object} options Export configuration
     * @param {Object} options.complianceData Full compliance data
     * @param {Array} options.groupedDeviations Deviations grouped by category
     * @param {String} options.clientCode Client identifier
     * @param {Object} options.filters What to include in export
     * @param {String} options.bannerImage Base64 image data (optional)
     */
    async exportToPDF(options) {
        const {
            complianceData,
            groupedDeviations,
            clientCode,
            filters,
            bannerImage
        } = options;

        const doc = new jsPDF();
        let yPosition = 20;

        // Add banner if provided
        if (bannerImage) {
            yPosition = this.addBanner(doc, bannerImage, yPosition);
        }

        // Add title
        yPosition = this.addTitle(doc, clientCode, yPosition);

        // Add executive summary
        if (filters.includeSummary) {
            yPosition = this.addExecutiveSummary(doc, complianceData, yPosition);
        }

        // Filter deviations based on user selections
        const filteredDeviations = this.filterDeviations(groupedDeviations, filters);

        // Add category breakdown
        if (filters.includeCategoryBreakdown) {
            yPosition = this.addCategoryBreakdown(doc, filteredDeviations, yPosition);
        }

        // Add detailed deviations
        if (filters.includeDetailedDeviations) {
            this.addDetailedDeviations(doc, filteredDeviations, yPosition);
        }

        // Add footer to all pages
        this.addFooters(doc, clientCode);

        // Generate filename
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `${clientCode}_Baseline_Compliance_Report_${timestamp}.pdf`;

        // Save the PDF
        doc.save(filename);

        return filename;
    }

    addBanner(doc, bannerImage, yPosition) {
        try {
            const pageWidth = doc.internal.pageSize.getWidth();
            const bannerHeight = 30;

            doc.addImage(bannerImage, 'PNG', 0, yPosition, pageWidth, bannerHeight);

            return yPosition + bannerHeight + 10;
        } catch (error) {
            console.error('Failed to add banner:', error);
            return yPosition;
        }
    }

    addTitle(doc, clientCode, yPosition) {
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...this.primaryColor);
        doc.text('Baseline Compliance Report', pageWidth / 2, yPosition, { align: 'center' });

        yPosition += 10;

        doc.setFontSize(14);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Client: ${clientCode}`, pageWidth / 2, yPosition, { align: 'center' });

        yPosition += 5;

        doc.setFontSize(10);
        const reportDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
        doc.text(`Generated: ${reportDate}`, pageWidth / 2, yPosition, { align: 'center' });

        return yPosition + 15;
    }

    addExecutiveSummary(doc, complianceData, yPosition) {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Check if we need a new page
        if (yPosition > pageHeight - 80) {
            doc.addPage();
            yPosition = 20;
        }

        // Section header
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Executive Summary', 20, yPosition);
        yPosition += 10;

        // Compliance score box
        const boxX = 20;
        const boxWidth = pageWidth - 40;
        const boxHeight = 35;

        // FIX: Use correct property names
        const complianceScore = complianceData.compliancePercentage || 0;
        const scoreColor = this.getScoreColor(complianceScore);

        doc.setFillColor(...scoreColor);
        doc.roundedRect(boxX, yPosition, boxWidth, boxHeight, 3, 3, 'F');

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('Overall Compliance Score', boxX + 10, yPosition + 10);

        doc.setFontSize(32);
        doc.text(`${complianceScore.toFixed(1)}%`, boxX + 10, yPosition + 28);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const scoreText = this.getScoreDescription(complianceScore);
        doc.text(scoreText, boxX + boxWidth - 10, yPosition + 22, { align: 'right', maxWidth: boxWidth - 70 });

        yPosition += boxHeight + 15;

        // FIX: Use correct property names from your Vue component
        const stats = [
            { label: 'Total Policies', value: complianceData.totalCount || 0, color: [100, 100, 100] },
            { label: 'Matched Policies', value: complianceData.compliantCount || 0, color: this.lowColor },
            { label: 'Total Deviations', value: complianceData.deviationCount || 0, color: [100, 100, 100] },
            { label: 'Critical', value: complianceData.criticalCount || 0, color: this.criticalColor },
            { label: 'High', value: complianceData.highCount || 0, color: this.highColor },
            { label: 'Medium', value: complianceData.mediumCount || 0, color: this.mediumColor },
            { label: 'Low', value: complianceData.lowCount || 0, color: this.lowColor }
        ];

        const colWidth = (pageWidth - 40) / 4;
        let xPos = 20;

        stats.forEach((stat, index) => {
            if (index > 0 && index % 4 === 0) {
                xPos = 20;
                yPosition += 25;
            }

            // Stat box
            doc.setFillColor(248, 249, 250);
            doc.rect(xPos, yPosition, colWidth - 5, 20, 'F');

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(stat.label, xPos + 5, yPosition + 7);

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...stat.color);
            doc.text(stat.value.toString(), xPos + 5, yPosition + 16);

            xPos += colWidth;
        });

        return yPosition + 30;
    }

    addCategoryBreakdown(doc, groupedDeviations, yPosition) {
        const pageHeight = doc.internal.pageSize.getHeight();

        // Check if we need a new page
        if (yPosition > pageHeight - 60) {
            doc.addPage();
            yPosition = 20;
        }

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Deviations by Category', 20, yPosition);
        yPosition += 10;

        // Prepare table data
        const tableData = groupedDeviations.map(group => {
            return [
                group.category,
                group.deviationCount.toString(),
                group.criticalCount.toString(),
                group.highCount.toString(),
                group.mediumCount.toString(),
                group.lowCount ? group.lowCount.toString() : '0'
            ];
        });

        doc.autoTable({
            startY: yPosition,
            head: [['Category', 'Total', 'Critical', 'High', 'Medium', 'Low']],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fillColor: this.primaryColor,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 10
            },
            bodyStyles: {
                fontSize: 9
            },
            columnStyles: {
                0: { cellWidth: 80 },
                1: { halign: 'center', cellWidth: 20 },
                2: { halign: 'center', cellWidth: 20, textColor: this.criticalColor },
                3: { halign: 'center', cellWidth: 20, textColor: this.highColor },
                4: { halign: 'center', cellWidth: 20, textColor: this.mediumColor },
                5: { halign: 'center', cellWidth: 20, textColor: this.lowColor }
            },
            margin: { left: 20, right: 20 }
        });

        return doc.lastAutoTable.finalY + 15;
    }

    addDetailedDeviations(doc, groupedDeviations, yPosition) {
        const pageHeight = doc.internal.pageSize.getHeight();

        // Check if we need a new page
        if (yPosition > pageHeight - 60) {
            doc.addPage();
            yPosition = 20;
        }

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Detailed Deviations', 20, yPosition);
        yPosition += 10;

        groupedDeviations.forEach(group => {
            // Check if we need a new page for this category
            if (yPosition > pageHeight - 100) {
                doc.addPage();
                yPosition = 20;
            }

            // Category header
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...this.primaryColor);
            doc.text(group.category, 20, yPosition);
            yPosition += 8;

            // Process each deviation in the category
            group.deviations.forEach(deviation => {
                // Check page space
                const estimatedHeight = 30 + (deviation.deviations?.length || 0) * 15;
                if (yPosition + estimatedHeight > pageHeight - 20) {
                    doc.addPage();
                    yPosition = 20;
                }

                // Deviation header with severity badge
                doc.setFillColor(240, 240, 240);
                doc.rect(20, yPosition, 170, 8, 'F');

                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(0, 0, 0);
                doc.text(deviation.policyName || 'Unnamed Policy', 22, yPosition + 6);

                // Severity badge
                const severityColor = this.getSeverityColor(deviation.highestSeverity);
                doc.setFillColor(...severityColor);
                doc.roundedRect(155, yPosition + 1, 30, 6, 1, 1, 'F');

                doc.setFontSize(8);
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.text(
                    (deviation.highestSeverity || 'LOW').toUpperCase(),
                    170,
                    yPosition + 5,
                    { align: 'center' }
                );

                yPosition += 12;

                // Property deviations table
                if (deviation.deviations && deviation.deviations.length > 0) {
                    const deviationData = deviation.deviations.map(dev => {
                        const baseValue = this.formatValue(dev.baselineValue);
                        const clientValue = this.formatValue(dev.clientValue);

                        return [
                            dev.property,
                            baseValue,
                            clientValue,
                            (dev.severity || 'low').toUpperCase()
                        ];
                    });

                    doc.autoTable({
                        startY: yPosition,
                        head: [['Property', 'Baseline Value', 'Client Value', 'Severity']],
                        body: deviationData,
                        theme: 'striped',
                        headStyles: {
                            fillColor: [100, 100, 100],
                            textColor: [255, 255, 255],
                            fontSize: 8,
                            fontStyle: 'bold'
                        },
                        bodyStyles: {
                            fontSize: 7
                        },
                        columnStyles: {
                            0: { cellWidth: 50, fontStyle: 'bold' },
                            1: { cellWidth: 50 },
                            2: { cellWidth: 50 },
                            3: {
                                cellWidth: 20,
                                halign: 'center',
                                fontStyle: 'bold'
                            }
                        },
                        didParseCell: (data) => {
                            if (data.column.index === 3 && data.section === 'body') {
                                const severity = data.cell.raw.toLowerCase();
                                const color = this.getSeverityColor(severity);
                                data.cell.styles.textColor = color;
                            }
                        },
                        margin: { left: 20, right: 20 }
                    });

                    yPosition = doc.lastAutoTable.finalY + 10;
                }

                yPosition += 5;
            });

            yPosition += 5;
        });

        return yPosition;
    }

    filterDeviations(groupedDeviations, filters) {
        let filtered = [...groupedDeviations];

        // Filter by severity
        if (filters.severityFilter && filters.severityFilter !== 'all') {
            filtered = filtered.map(group => ({
                ...group,
                deviations: group.deviations.filter(dev => {
                    const severity = dev.highestSeverity || dev.severity;

                    if (filters.severityFilter === 'criticalHigh') {
                        return severity === 'critical' || severity === 'high';
                    }

                    return severity === filters.severityFilter;
                })
            })).filter(group => group.deviations.length > 0);
        }

        // Filter by categories
        if (filters.categories && filters.categories.length > 0) {
            filtered = filtered.filter(group =>
                filters.categories.includes(group.category)
            );
        }

        // Recalculate counts after filtering
        filtered = filtered.map(group => {
            const criticalCount = group.deviations.filter(d =>
                (d.highestSeverity || d.severity) === 'critical'
            ).length;
            const highCount = group.deviations.filter(d =>
                (d.highestSeverity || d.severity) === 'high'
            ).length;
            const mediumCount = group.deviations.filter(d =>
                (d.highestSeverity || d.severity) === 'medium'
            ).length;
            const lowCount = group.deviations.filter(d =>
                (d.highestSeverity || d.severity) === 'low'
            ).length;

            return {
                ...group,
                deviationCount: group.deviations.length,
                criticalCount,
                highCount,
                mediumCount,
                lowCount
            };
        });

        return filtered;
    }

    addFooters(doc, clientCode) {
        const pageCount = doc.internal.getNumberOfPages();

        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(150, 150, 150);

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // Left side - client code
            doc.text(`${clientCode} | Baseline Compliance Report`, 20, pageHeight - 10);

            // Right side - page number
            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth - 20,
                pageHeight - 10,
                { align: 'right' }
            );

            // Center - confidential notice
            doc.text(
                'CONFIDENTIAL',
                pageWidth / 2,
                pageHeight - 10,
                { align: 'center' }
            );
        }
    }

    // Helper methods
    getSeverityColor(severity) {
        const colors = {
            critical: this.criticalColor,
            high: this.highColor,
            medium: this.mediumColor,
            low: this.lowColor,
            prerequisite: this.prerequisiteColor
        };
        return colors[severity?.toLowerCase()] || this.lowColor;
    }

    getScoreColor(percentage) {
        if (percentage >= 90) return [40, 167, 69]; // Green
        if (percentage >= 75) return [255, 193, 7]; // Yellow
        return [220, 53, 69]; // Red
    }

    getScoreDescription(percentage) {
        if (percentage >= 90) return 'Excellent compliance with baseline';
        if (percentage >= 75) return 'Good compliance with some deviations';
        if (percentage >= 50) return 'Moderate compliance - review required';
        return 'Low compliance - immediate attention needed';
    }

    formatValue(value) {
        if (value === null || value === undefined) return 'null';
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        if (typeof value === 'object') {
            return JSON.stringify(value).substring(0, 50) + '...';
        }
        return String(value).substring(0, 50);
    }
}

export default new PDFExportService();